import { IconButton, Tooltip } from '@radix-ui/themes';
import { evaluationIconTexts } from 'components/Templates/builder/Evaluation/constants';
import { BadgeCheck, CircleCheck, CircleIcon, CircleX, CpuIcon, Info, Star, TriangleAlert, Weight } from 'lucide-react';
import type { ComponentProps } from 'react';
import type { TEvaluation } from 'types/evaluation.types';
import { EEvaluationType } from 'types/evaluation.types';

interface Props extends ComponentProps<typeof IconButton> {
  evaluation: TEvaluation;
  threshold?: number;
}

const EvaluationIcons = ({ evaluation, threshold, ...props }: Props) => {
  const points = evaluation.points || evaluation.points === 0 ? parseInt(evaluation.points.toFixed(0)) : 0;
  const bonusPoints =
    evaluation.bonus_points || evaluation.bonus_points === 0 ? parseInt(evaluation.bonus_points.toFixed(0)) : 0;
  const weight =
    evaluation.weight || evaluation.weight === 0 ? parseInt(Math.ceil(evaluation.weight * 100).toFixed(0)) : 0;

  return (
    <IconButton {...props} className="w-8 bg-transparent" style={{ backgroundColor: 'transparent' }}>
      {(() => {
        if (evaluation?.name === EEvaluationType.Info) {
          return (
            <Tooltip content={evaluationIconTexts.Info}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <Info className="text-evaluation-info h-8 w-8" />
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Warning) {
          return (
            <Tooltip content={evaluationIconTexts.Warning}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <TriangleAlert className="text-evaluation-warning h-8 w-8" />
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Qualified) {
          return (
            <Tooltip content={evaluationIconTexts.Qualified}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <CircleCheck className="text-evaluation-qualified h-8 w-8" />
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.AutoQualified) {
          return (
            <Tooltip content={evaluationIconTexts.AutoQualified}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <BadgeCheck className="text-evaluation-qualified h-8 w-8" />
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Disqualified) {
          return (
            <Tooltip content={evaluationIconTexts.Disqualified}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <CircleX className="text-evaluation-disqualified h-8 w-8" />
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Bonus) {
          return (
            <Tooltip content={`This option results in ${bonusPoints} bonus points. ${evaluationIconTexts.Bonus}`}>
              <div className={`flex font-semibold ${bonusPoints > 0 ? 'text-evaluation-bonus' : 'text-gray-500'}`}>
                <Star />
                <span>{bonusPoints}</span>
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Point) {
          return (
            <Tooltip content={`${points} base points. Threshold: ${threshold}. ${evaluationIconTexts.Point}`}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <CircleIcon
                  className={`rounded-full ${
                    threshold && points > threshold
                      ? 'text-evaluation-point bg-evaluation-point'
                      : 'bg-gray-300 text-gray-300'
                  }`}
                  size={'30px'}
                />
                <span
                  className="absolute text-sm font-semibold text-white"
                  style={{
                    fontSize: '0.75rem',
                    right: `${points && points > 9 ? '29%' : points === 1 ? '42%' : '38%'}`,
                    top: '16%',
                  }}
                >
                  {points ?? 0}
                </span>
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === EEvaluationType.Weighted) {
          return (
            <Tooltip content={`This option results in ${weight} points. ${evaluationIconTexts.Point}`}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <Weight className="text-primary" size="30px" />
                <span
                  className="vertical-center absolute top-[2%] right-0 text-black"
                  style={{
                    fontSize: '0.75rem',
                    right: `${weight && weight > 9 ? '29%' : weight === 1 ? '42%' : '38%'}`,
                    top: '16%',
                  }}
                >
                  {weight ?? 0}
                </span>
              </div>
            </Tooltip>
          );
        } else if (evaluation?.name === 'ai') {
          return (
            <Tooltip content={`${evaluationIconTexts.AiDetection}`}>
              <div className="relative w-fit" style={{ height: 'inherit', width: 'inherit' }}>
                <CpuIcon className="text-warning" size="30px" />
              </div>
            </Tooltip>
          );
        }
      })()}
    </IconButton>
  );
};

export default EvaluationIcons;
